import { SolanaAddress, type useWallet, type useWalletModal } from "@solomon-apps/shared";
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";

import { useClientContextKey } from "@/hooks/useClientContextKey";
import { issueWalletEnrollmentChallenge } from "@/services/clientWalletEnrollment";
import type { UseVerifyActionFlowResult } from "@/verification";
import { useVerifyActionFlow } from "@/verification";

import { positionSubmitErrorCopy } from "../errorCopy";
import { addressEditResetState } from "../stepFlowState";
import type { VerifyStatus } from "../steps";
import type { VerifiedSource } from "../types";

import type { usePositionDiscovery } from "./usePositionDiscovery";
import type { usePositionSource } from "./usePositionSource";
import { type BoundPositionChallenge, usePositionSubmission } from "./usePositionSubmission";
import { useRewardsDestination } from "./useRewardsDestination";

interface PositionVerificationOptions {
    address: string;
    setAddress: Dispatch<SetStateAction<string>>;
    setAddressTouched: Dispatch<SetStateAction<boolean>>;
    wallet: Pick<ReturnType<typeof useWallet>, "connected" | "disconnect"> & {
        connectedWalletAddress: string;
        openModal: ReturnType<typeof useWalletModal>["openModal"];
    };
    source: ReturnType<typeof usePositionSource>;
    discovery: ReturnType<typeof usePositionDiscovery>;
    issuerName: string | null;
}
interface UsePositionVerificationResult {
    verification: UseVerifyActionFlowResult;
    verificationError: string | null;
    recoveryReason: "expired" | "submit-failed" | null;
    verifyStatus: VerifyStatus;
    verifiedSource: VerifiedSource | null;
    retryReady: boolean;
    manualReviewDescription: string;
    setManualReviewDescription: Dispatch<SetStateAction<string>>;
    pendingReview: { uuid: string; address: string; description: string; issuerName: string } | null | undefined;
    handleVerify: () => void;
    handleVerifyAgain: () => void;
    handleVerifyDirect: () => void;
    onAddressChange: (next: string) => void;
    onAddressBlur: () => void;
    destination: string;
    setDestination: Dispatch<SetStateAction<string>>;
    handleSubmit: () => void;
    claimRequired: boolean;
    handleConfirmClaim: () => void;
    submitError: string | null;
    verificationExpired: boolean;
    registeredPositionCount: number | null;
    submitted: boolean;
    isSubmitting: boolean;
}

/** Owns the wallet ceremony and its captured proof, including the in-place
 * retry at Submit. Discovery and submission keep their own independent state. */
export function usePositionVerification({
    address,
    setAddress,
    setAddressTouched,
    wallet,
    source,
    discovery,
    issuerName
}: PositionVerificationOptions): UsePositionVerificationResult {
    const { connected, disconnect, connectedWalletAddress, openModal } = wallet;
    const { sourceInput, sourceKind, resolvedMultisig, memberCanVerify, membershipCheckPending, walletNeedsSwitch } =
        source;
    const { ensureDiscovered, resetDiscovery } = discovery;
    const verification = useVerifyActionFlow({ autoMicroTransferFallback: true });
    const { cancel: cancelVerification } = verification;
    const [verificationError, setVerificationError] = useState<string | null>(null);
    const [manualReviewDescription, setManualReviewDescription] = useState("");
    const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>("idle");
    const contextKey = useClientContextKey();
    const [positionChallenge, setPositionChallenge] = useState<BoundPositionChallenge | null>(null);
    const verificationRunRef = useRef(0);
    const verificationKey = JSON.stringify([contextKey, address.trim(), connectedWalletAddress]);
    const latestVerificationKey = useRef(verificationKey);
    latestVerificationKey.current = verificationKey;
    const previousVerificationKey = useRef(verificationKey);
    const previousContextKey = useRef(contextKey);
    const [verifiedSource, setVerifiedSource] = useState<VerifiedSource | null>(null);
    const { destination, setDestination } = useRewardsDestination();
    // After a verification failure the button shows "Verification Failed" for a beat, then
    // flips to an enabled "Retry" (default state) so the user can re-attempt.
    const [retryReady, setRetryReady] = useState(false);

    const {
        handleSubmit,
        claimRequired,
        handleConfirmClaim,
        submitError,
        setSubmitError,
        verificationExpired,
        resetRecovery,
        recoveryReason,
        registeredPositionCount,
        submitted,
        isSubmitting,
        pendingReview,
        resetSubmission
    } = usePositionSubmission({
        verifiedSource,
        positionChallenge,
        contextKey,
        destination,
        submissionKey: JSON.stringify([contextKey, address.trim()]),
        manualReviewDescription,
        issuerName
    });
    function clearVerification(): void {
        setVerifyStatus("idle");
        setVerificationError(null);
        setVerifiedSource(null);
        setPositionChallenge(null);
        verificationRunRef.current += 1;
        verification.cancel();
    }

    function buildChallengeInput():
        | {
              walletAddress: string;
              sourceKind: "eoa";
              signerAddress?: undefined;
              multisigProgramId?: undefined;
          }
        | {
              walletAddress: string;
              signerAddress: string;
              multisigProgramId: string;
              sourceKind: "multisig-squads";
          }
        | null {
        if (connectedWalletAddress.length === 0 || !sourceKind) return null;
        if (sourceKind === "eoa") {
            if (address && address !== connectedWalletAddress) return null;
            return {
                walletAddress: connectedWalletAddress,
                sourceKind
            };
        }
        if (!resolvedMultisig || !memberCanVerify || membershipCheckPending) return null;
        return {
            walletAddress: resolvedMultisig.address,
            signerAddress: connectedWalletAddress,
            multisigProgramId: resolvedMultisig.programId,
            sourceKind
        };
    }

    useEffect(() => {
        const contextChanged = previousContextKey.current !== contextKey;
        previousContextKey.current = contextKey;
        if (previousVerificationKey.current === verificationKey && !contextChanged) return;
        previousVerificationKey.current = verificationKey;
        setVerificationError(null);
        verificationRunRef.current += 1;
        if (contextChanged || verifyStatus === "verifying" || verifyStatus === "failed") {
            cancelVerification();
            setPositionChallenge(null);
            setVerifyStatus("idle");
        }
        if (contextChanged) {
            setDestination("");
            setManualReviewDescription("");
            resetSubmission();
            setSubmitError(null);
            resetRecovery();
            setVerifiedSource(null);
            resetDiscovery();
        }
    }, [
        contextKey,
        verificationKey,
        verifyStatus,
        cancelVerification,
        setDestination,
        resetSubmission,
        setSubmitError,
        resetRecovery,
        resetDiscovery
    ]);

    function handleVerify(): void {
        startVerification(false);
    }

    // Re-runs the ownership ceremony for the same source from the Submit step
    // once the captured proof has expired. The source, destination and review
    // stay as they are; on success the fresh proof replaces the old one in
    // `verifiedSource` (the success effect below) and the action returns to
    // submit. It does not submit on its own: submitting creates the position,
    // so it stays the user's click.
    function handleVerifyAgain(): void {
        startVerification(true);
    }

    // `reverify`: the Submit step's in-place re-verification, which runs over
    // an existing (expired) verification and keeps the expiry notice up until
    // it succeeds.
    function startVerification(reverify: boolean): void {
        if (verifyStatus === "verified" && !reverify) return;
        if (verifyStatus === "verifying" && verification.step !== "error") {
            return;
        }
        if (!connected || connectedWalletAddress.length === 0) {
            openModal();
            return;
        }
        // Connected with a wallet that can't act as the source — a different
        // wallet than the EOA source, or a non-signer of the multisig. Disconnect
        // and reopen the picker so the user can connect a wallet that can sign
        // (you can't sign for a wallet you're not connected as, and the picker
        // can't switch a connected wallet without disconnecting first).
        if (walletNeedsSwitch) {
            if (reverify) {
                // Keep the (expired) verification: the review is built on it.
                // Only the wallet changes; Verify Again runs once it's back.
                void disconnect().then(() => {
                    window.setTimeout(openModal, 0);
                });
            } else {
                switchWallet();
            }
            return;
        }
        if (sourceKind === null) {
            setAddressTouched(true);
            setVerifyStatus("failed");
            return;
        }

        verification.cancel();
        setPositionChallenge(null);
        setAddressTouched(true);
        if (!reverify) setSubmitError(null);
        setVerificationError(null);
        setVerifyStatus("verifying");

        const run = ++verificationRunRef.current;
        const capturedKey = verificationKey;
        void (async (): Promise<void> => {
            try {
                const discovery = await ensureDiscovered();
                if (run !== verificationRunRef.current || latestVerificationKey.current !== capturedKey) return;
                if (!("surfaceUuid" in discovery)) {
                    setVerifyStatus(discovery.stopped === "superseded" ? "idle" : "failed");
                    return;
                }
                const input = buildChallengeInput();
                if (!input) {
                    setVerifyStatus("failed");
                    return;
                }
                const challenge = await issueWalletEnrollmentChallenge(input);
                if (run !== verificationRunRef.current || latestVerificationKey.current !== capturedKey) return;
                if (
                    !Number.isFinite(Date.parse(challenge.expiresAt)) ||
                    Date.parse(challenge.expiresAt) <= Date.now() ||
                    !/^[a-f0-9]{64}$/.test(challenge.nonce) ||
                    challenge.microTransferMemo !== `solomon-yaas:v1:${challenge.nonce}`
                ) {
                    throw new Error("Position verification could not be started. Please retry.");
                }
                setPositionChallenge({
                    ...challenge,
                    contextKey
                });
                verification.startFlow({
                    address: connectedWalletAddress,
                    messageToSign: challenge.message,
                    method: "signMessage",
                    microTransferMemo: challenge.microTransferMemo
                });
            } catch (error) {
                if (run !== verificationRunRef.current || latestVerificationKey.current !== capturedKey) return;
                const message = positionSubmitErrorCopy(error);
                setVerificationError(message);
                if (reverify) setSubmitError(message);
                setVerifyStatus("failed");
            }
        })();
    }

    function handleVerifyDirect(): void {
        if (verifyStatus === "verified" || verifyStatus === "verifying") return;
        if (!SolanaAddress.isShape(sourceInput)) {
            setAddressTouched(true);
            return;
        }
        setVerificationError(null);
        verification.cancel();
        setPositionChallenge(null);
        setAddressTouched(true);
        setSubmitError(null);
        setVerifyStatus("verifying");
        const run = ++verificationRunRef.current;
        const capturedKey = verificationKey;

        void (async (): Promise<void> => {
            const discovery = await ensureDiscovered(true);
            if (run !== verificationRunRef.current || latestVerificationKey.current !== capturedKey) return;
            if (!("surfaceUuid" in discovery)) {
                setVerifyStatus(discovery.stopped === "superseded" ? "idle" : "failed");
                return;
            }
            if (resolvedMultisig !== null) {
                setVerifiedSource({
                    kind: "multisig-squads",
                    multisigAddress: resolvedMultisig.address,
                    multisigProgramId: resolvedMultisig.programId,
                    vaultAddress: resolvedMultisig.vaultAddress,
                    verificationMode: "direct-solomon"
                });
            } else {
                setVerifiedSource({
                    kind: sourceKind === "eoa" ? "eoa" : "manual",
                    address: sourceInput,
                    verificationMode: "direct-solomon"
                });
            }
            // This satisfies navigation, not proof of ownership. Submission
            // persists a Pending wallet which only the issuer can approve.
            setVerifyStatus("verified");
        })();
    }

    // Backs the "Switch Wallet" action (EOA mismatch or multisig non-signer):
    // clear any in-flight / captured verification (it's bound to the wallet we're
    // dropping), disconnect, then reopen the picker on the next tick so the
    // disconnect settles and the modal's wallet rows are live again (the picker
    // can't switch an already-connected wallet on its own).
    function switchWallet(): void {
        clearVerification();
        void disconnect().then(() => {
            window.setTimeout(openModal, 0);
        });
    }

    function onAddressChange(next: string): void {
        setAddress(next);
        setManualReviewDescription("");
        resetSubmission();
        verificationRunRef.current += 1;
        // Editing the source invalidates discovery AND any captured verification
        // (the signed message binds the old wallet), so both step gates fall
        // back to unsatisfied — the user can't advance on a stale proof.
        const reset = addressEditResetState();
        resetDiscovery();
        setVerificationError(null);
        setSubmitError(reset.submitError);
        resetRecovery();
        setVerifyStatus(reset.verifyStatus);
        setVerifiedSource(reset.verifiedSource);
        // Invalidate any discovery response still in flight for the old input.
        // Cancel any in-flight verification ceremony (the imperative effect the
        // reset value can't express).
        verification.cancel();
    }

    function onAddressBlur(): void {
        setAddressTouched(true);
    }

    useEffect(() => {
        if (verification.step === null) return;
        if (verification.step === "error") {
            setVerifyStatus("failed");
            return;
        }
        if (verification.step !== "success") {
            setVerifyStatus("verifying");
        }
    }, [verification.step]);

    // Hold the "Verification Failed" label for 3s, then promote the button to an enabled
    // "Retry". Any non-failed status clears the grace immediately (a fresh
    // attempt, a reset, or success shouldn't show a stale Retry).
    useEffect(() => {
        if (verifyStatus !== "failed") {
            setRetryReady(false);
            return;
        }
        const timer = setTimeout(() => setRetryReady(true), 3000);
        return (): void => clearTimeout(timer);
    }, [verifyStatus]);

    useEffect(() => {
        if (
            verification.step !== "success" ||
            !verification.proof ||
            !positionChallenge ||
            verifyStatus === "verified"
        ) {
            return;
        }
        // A fresh proof ends an expiry (the Submit step's Verify Again).
        resetRecovery();
        setSubmitError(null);

        if (sourceKind === "eoa") {
            if (!connectedWalletAddress) return;
            setVerifiedSource({
                kind: "eoa",
                address: connectedWalletAddress,
                proof: { ...verification.proof, verificationNonce: positionChallenge?.nonce }
            });
            setAddress(connectedWalletAddress);
            setVerifyStatus("verified");
            return;
        }

        if (resolvedMultisig === null || !connectedWalletAddress) {
            return;
        }
        setVerifiedSource({
            kind: "multisig-squads",
            multisigAddress: resolvedMultisig.address,
            signerAddress: connectedWalletAddress,
            multisigProgramId: resolvedMultisig.programId,
            vaultAddress: resolvedMultisig.vaultAddress,
            proof: { ...verification.proof, verificationNonce: positionChallenge?.nonce }
        });
        // Keep the source the user entered (account or vault) — see the
        // direct-verification path above; the canonical address is captured in
        // `verifiedSource`, so the field shouldn't be rewritten.
        setVerifyStatus("verified");
    }, [
        connectedWalletAddress,
        resolvedMultisig,
        sourceKind,
        verification.proof,
        verification.step,
        verifyStatus,
        positionChallenge,
        resetRecovery,
        setSubmitError,
        setAddress
    ]);

    // Note: switching the connected wallet after a source is verified does NOT
    // invalidate it. The verification proof is already captured, and submit is a
    // pure API call (no further signing or onchain action), so there's no reason
    // to force a re-verify just because the active wallet changed.

    return {
        verification,
        verificationError,
        recoveryReason,
        verifyStatus,
        verifiedSource,
        retryReady,
        manualReviewDescription,
        setManualReviewDescription,
        pendingReview,
        handleVerify,
        handleVerifyAgain,
        handleVerifyDirect,
        onAddressChange,
        onAddressBlur,
        destination,
        setDestination,
        handleSubmit,
        claimRequired,
        handleConfirmClaim,
        submitError,
        verificationExpired,
        registeredPositionCount,
        submitted,
        isSubmitting
    };
}
